/**
 * Seções de atividade da Tela 2 (T2-2): Conversas · Appointments · Pendências.
 *
 * Conversas/Appointments consomem os sub-endpoints paginados REAIS. Stats são
 * derivados do conjunto CARREGADO (a paginação não traz total) com rótulo
 * honesto (D2/D3). Pendências expõe só a contagem do detalhe — a lista por
 * pessoa é honest-defer (Item 338).
 */

import { useNavigate } from 'react-router-dom';

import {
  conversationStats,
  conversationStatusLabel,
  conversationOutcomeLabel,
  appointmentStats,
  appointmentStatusLabel,
  appointmentBadgeVariant,
  channelLabel,
  formatShortDate,
  formatDateTime,
  TERMINAL_CONVERSATION_STATUSES,
} from './persons-adapter';
import { PersonSection } from './PersonSection';
import { usePersonConversations, usePersonAppointments, usePersonWaitlist } from './use-persons';

import type { PersonConversation } from './types';

// ─── 4. Conversas ───────────────────────────────────────────────────────

/**
 * Sublinha da conversa na ficha. N2-18 (noturna 2, 05/Set/2026, D7/D8): a ficha listava
 * «aguardando cliente · Resolvida» — status VIVO + desfecho do fechamento ANTERIOR lado a lado,
 * sem dizer que o segundo é histórico. Conversa viva (não-terminal) com desfecho anterior é uma
 * REABERTA: «reaberta · <status> · último desfecho: <desfecho>». Terminal: copy de antes.
 */
export function conversationSubline(c: PersonConversation): string {
  const status = conversationStatusLabel(c.status);
  if (c.outcomeType == null) return status;
  const outcome = conversationOutcomeLabel(c.outcomeType);
  if (TERMINAL_CONVERSATION_STATUSES.has(c.status)) return `${status} · ${outcome}`;
  return `reaberta · ${status} · último desfecho: ${outcome}`;
}

export function ConversasSection({ personId }: { personId: string }): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading } = usePersonConversations(personId);
  const convs = data?.data ?? [];
  const stats = conversationStats(convs);
  const hasMore = data?.meta.hasMore ?? false;

  return (
    <PersonSection
      id="conversas"
      label="Conversas"
      count={hasMore ? `${stats.loaded}+` : stats.loaded}
    >
      <div className="person-stat-row">
        <div className="person-stat-row__col">
          <span className="person-stat-row__num">{stats.loaded}</span>
          <span className="person-stat-row__lbl">carregadas</span>
        </div>
        <div className="person-stat-row__col">
          <span className="person-stat-row__num">{stats.active}</span>
          <span className="person-stat-row__lbl">ativas</span>
        </div>
      </div>
      {isLoading ? (
        <div className="person-empty">Carregando conversas…</div>
      ) : convs.length === 0 ? (
        <div className="person-empty">Nenhuma conversa registrada para esta pessoa.</div>
      ) : (
        <ul className="person-mini-list">
          {convs.map((c) => (
            <li
              key={c.id}
              className="person-mini-list__item"
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/atendimento/conversation/${c.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/atendimento/conversation/${c.id}`);
                }
              }}
            >
              <span className="person-mini-list__time">
                {formatShortDate(c.closedAt ?? c.updatedAt)}
              </span>
              <div className="person-mini-list__body">
                <div className="person-mini-list__title">Conversa · {channelLabel(c.channel)}</div>
                <div className="person-mini-list__sub mono">{conversationSubline(c)}</div>
                {/* F7 — excerto narrativo (LivingSummary); honest-defer: só quando existe. */}
                {c.excerpt != null && c.excerpt.length > 0 && (
                  <div className="person-mini-list__excerpt">{c.excerpt}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </PersonSection>
  );
}

// ─── 5. Pendências (só contagem — lista honest-defer Item 338) ──────────

export function PendenciasSection({ pendencyCount }: { pendencyCount: number }): JSX.Element {
  return (
    <PersonSection id="pendencias" label="Pendências" count={pendencyCount}>
      <div className="person-stat-row">
        <div className="person-stat-row__col">
          <span
            className={
              'person-stat-row__num' + (pendencyCount > 0 ? ' person-stat-row__num--warn' : '')
            }
          >
            {pendencyCount}
          </span>
          <span className="person-stat-row__lbl">vinculadas</span>
        </div>
      </div>
      <div className="person-empty">
        A lista detalhada de pendências por pessoa ainda não está disponível (a contagem deriva das
        conversas e bonds da pessoa). Acompanhe pelas conversas vinculadas.
      </div>
    </PersonSection>
  );
}

// ─── 5b. Fila de espera (Frente B Sessão 3, gated `scheduling-waitlist`) ──
//
// Read-only na ficha 360º: "em que filas esta pessoa está". A GESTÃO (aprovar/
// liberar/reordenar) mora na /agenda (por recurso) — aqui é só visibilidade,
// coerente com a ficha ser read-only. Reusa o endpoint da Sessão 2 (personId).

const WAITLIST_STATUS_LABEL: Record<string, string> = {
  pending_approval: 'aguardando liberação',
  active: 'na fila',
  offered: 'vaga ofertada',
};
const WAITLIST_TIER_LABEL: Record<string, string> = {
  bumped: 'reposicionado',
  urgent_high: 'urgência alta',
  urgent_medium: 'urgência média',
  urgent_low: 'urgência baixa',
  preferred: 'prioritário',
  normal: 'normal',
};
const WAITLIST_LIVE = new Set(['pending_approval', 'active', 'offered']);

export function WaitlistSection({ personId }: { personId: string }): JSX.Element {
  const { data, isLoading } = usePersonWaitlist(personId);
  const live = data.filter((e) => WAITLIST_LIVE.has(e.status));

  return (
    <PersonSection id="waitlist" label="Fila de espera" count={live.length}>
      {isLoading ? (
        <div className="person-empty">Carregando filas…</div>
      ) : live.length === 0 ? (
        <div className="person-empty">Esta pessoa não está em nenhuma fila de espera.</div>
      ) : (
        <ul className="person-mini-list">
          {live.map((e) => (
            <li key={e.id} className="person-mini-list__item">
              <div className="person-mini-list__body">
                <div className="person-mini-list__title">{e.serviceName ?? 'Serviço'}</div>
                <div className="person-mini-list__sub mono">
                  {WAITLIST_STATUS_LABEL[e.status] ?? e.status}
                  {e.priorityTier !== 'normal' &&
                    ` · ${WAITLIST_TIER_LABEL[e.priorityTier] ?? e.priorityTier}`}
                  {e.status === 'offered' &&
                    e.offerExpiresAt !== null &&
                    ` · expira ${formatDateTime(e.offerExpiresAt)}`}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </PersonSection>
  );
}

// ─── 6. Appointments (gated `appointments`) ─────────────────────────────

export function AppointmentsSection({ personId }: { personId: string }): JSX.Element {
  const { data, isLoading } = usePersonAppointments(personId);
  const appts = data?.data ?? [];
  const hasMore = data?.meta.hasMore ?? false;
  const stats = appointmentStats(appts, new Date());

  return (
    <PersonSection
      id="appointments"
      label="Agendamentos"
      count={hasMore ? `${appts.length}+` : appts.length}
    >
      <div className="person-stat-row">
        <div className="person-stat-row__col">
          <span className="person-stat-row__num">{stats.upcoming}</span>
          <span className="person-stat-row__lbl">próximos</span>
        </div>
        <div className="person-stat-row__col">
          <span className="person-stat-row__num">{stats.completed}</span>
          <span className="person-stat-row__lbl">concluídos</span>
        </div>
        <div className="person-stat-row__col">
          <span
            className={
              'person-stat-row__num' + (stats.rescheduled > 0 ? ' person-stat-row__num--warn' : '')
            }
          >
            {stats.rescheduled}
          </span>
          <span className="person-stat-row__lbl">reagendados</span>
        </div>
        <div className="person-stat-row__col">
          <span
            className={
              'person-stat-row__num' + (stats.noShow > 0 ? ' person-stat-row__num--danger' : '')
            }
          >
            {stats.noShow}
          </span>
          <span className="person-stat-row__lbl">faltas</span>
        </div>
      </div>
      <div className="muted mono" style={{ fontSize: 10 }}>
        Contadores derivados dos agendamentos carregados.
      </div>
      {isLoading ? (
        <div className="person-empty">Carregando agendamentos…</div>
      ) : appts.length === 0 ? (
        <div className="person-empty">Nenhum agendamento registrado para esta pessoa.</div>
      ) : (
        <ul className="person-mini-list">
          {appts.map((a) => (
            <li key={a.id} className="person-mini-list__item">
              <span className="person-mini-list__time">{formatDateTime(a.scheduledAt)}</span>
              <div className="person-mini-list__body">
                <div className="person-mini-list__title">{a.title}</div>
                <div className="person-mini-list__sub mono">
                  {a.addressOrLocation ?? 'sem local'} · {appointmentStatusLabel(a.status)}
                </div>
              </div>
              <span className={`badge badge--${appointmentBadgeVariant(a.status)}`}>
                <span className="badge__dot" aria-hidden />
                {appointmentStatusLabel(a.status)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </PersonSection>
  );
}
